import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { BatchPackageState, BatchReturnStatusType, ReturnStatusType } from 'returnCenter-api';
import useAppDispatch from 'hooks/useAppDispatch';
import useAppSelector from 'hooks/useAppSelector';
import {
    createReturnSlipForPackage,
    fetchBatchReturnById,
    fetchProcessingSummary,
    markPackageMissing,
    selectCurrentBatchReturn,
    selectProcessingSummary,
    unmarkPackageMissing,
} from 'store/reducers/batchReturnSlice';
import { companiesSelector } from 'store/reducers/companiesSlice';
import { BatchPackage, CompanyProcessingGroup } from 'types/batchReturnProcessing';
import { getStatusTypeString } from 'utils/helpers';

const toastOptions = { autoClose: 3500, closeOnClick: true };

export type BatchPackageView = {
    company: CompanyProcessingGroup;
    pkg: BatchPackage;
    /** The caller is the company that received this package, and the batch is not completed. */
    canAct: boolean;
};

/**
 * The packages of the batch on screen, as the receiving companies see them, and what the caller
 * can do with them. Shared by the package list and the side panel so both act the same way.
 */
const useBatchPackages = () => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const summary = useAppSelector(selectProcessingSummary);
    const batchReturn = useAppSelector(selectCurrentBatchReturn);
    const selectedCompany = useAppSelector(companiesSelector.selectedCompany);
    const selectedMarketId = useAppSelector(companiesSelector.selectSelectedMarketId);
    const [busyPackage, setBusyPackage] = useState<number | null>(null);

    const batchReturnId = batchReturn?.id;
    const batchCompleted = summary?.batchStatus === BatchReturnStatusType.Completed;

    /** The sending company: the only one that can open the source returns. */
    const isSender =
        batchReturn != null &&
        (batchReturn.contextId ?? undefined) === selectedCompany &&
        (batchReturn.marketId ?? undefined) === selectedMarketId;

    // Only the company that received its share may act on it; everyone else sees progress.
    const isOwnCompany = useCallback(
        (company: CompanyProcessingGroup) =>
            company.processingRecords.some(
                (r) => r.contextId === selectedCompany && (r.marketId ?? undefined) === selectedMarketId,
            ),
        [selectedCompany, selectedMarketId],
    );

    const packagesFor = useCallback(
        (sourceReturnId: number): BatchPackageView[] =>
            (summary?.companies ?? []).flatMap((company) =>
                company.packages
                    .filter((pkg) => pkg.sourceReturnId === sourceReturnId)
                    .map((pkg) => ({ company, pkg, canAct: isOwnCompany(company) && !batchCompleted })),
            ),
        [summary, isOwnCompany, batchCompleted],
    );

    const refresh = useCallback(() => {
        if (!batchReturnId) return;
        dispatch(fetchProcessingSummary(batchReturnId));
        dispatch(fetchBatchReturnById(batchReturnId));
    }, [dispatch, batchReturnId]);

    const navigateToReturn = useCallback(
        (returnId: number, status: ReturnStatusType | null | undefined) => {
            const statusKey = getStatusTypeString(status ?? undefined);
            if (!statusKey) return;
            navigate(`/returns/${statusKey}/${returnId}`, {
                state: { origin: statusKey, returnIdFromPath: returnId, returnStatus: status },
            });
        },
        [navigate],
    );

    const openReturnSlip = useCallback(
        (pkg: BatchPackage) => {
            if (pkg.returnSlipId != null) navigateToReturn(pkg.returnSlipId, pkg.returnSlipStatus);
        },
        [navigateToReturn],
    );

    const showError = useCallback(
        (message?: string) =>
            toast.error(message ?? t('batchReturn.actionFailed', 'Action failed. Please try again.'), toastOptions),
        [t],
    );

    const createSlip = useCallback(
        async (pkg: BatchPackage) => {
            if (!batchReturnId) return;
            setBusyPackage(pkg.sourceReturnId);
            const result = await dispatch(
                createReturnSlipForPackage({ batchReturnId, sourceReturnId: pkg.sourceReturnId }),
            );
            setBusyPackage(null);

            if (createReturnSlipForPackage.fulfilled.match(result)) {
                navigateToReturn(result.payload.returnId, result.payload.returnStatus);
            } else {
                showError(result.payload?.message);
                refresh();
            }
        },
        [dispatch, batchReturnId, navigateToReturn, showError, refresh],
    );

    const toggleMissing = useCallback(
        async (pkg: BatchPackage) => {
            if (!batchReturnId) return;
            setBusyPackage(pkg.sourceReturnId);
            const args = { batchReturnId, sourceReturnId: pkg.sourceReturnId };
            const result =
                pkg.state === BatchPackageState.Missing
                    ? await dispatch(unmarkPackageMissing(args))
                    : await dispatch(markPackageMissing(args));
            setBusyPackage(null);

            if ('error' in result) showError(result.payload?.message);
            refresh();
        },
        [dispatch, batchReturnId, showError, refresh],
    );

    const packageStateLabel = useCallback(
        (pkg: BatchPackage) => {
            // While being credited, or after a failed credit, the slip's status says more than the rows.
            if (pkg.returnSlipStatus === ReturnStatusType.Processing) {
                return t('batchReturn.packageState.crediting', 'Being credited');
            }
            if (pkg.returnSlipStatus === ReturnStatusType.CreditError) {
                return t('batchReturn.packageState.creditFailed', 'Credit failed');
            }
            switch (pkg.state) {
                case BatchPackageState.SlipCreated:
                    return t('batchReturn.packageState.slipCreated', 'Return slip created');
                case BatchPackageState.Handled:
                    return t('batchReturn.packageState.handled', 'Handled');
                case BatchPackageState.Credited:
                    return t('batchReturn.packageState.credited', 'Credited');
                case BatchPackageState.Missing:
                    return t('batchReturn.packageState.missing', 'Missing');
                default:
                    return t('batchReturn.packageState.notStarted', 'Not started');
            }
        },
        [t],
    );

    return {
        summary,
        batchCompleted,
        isSender,
        isOwnCompany,
        packagesFor,
        busyPackage,
        refresh,
        showError,
        navigateToReturn,
        openReturnSlip,
        createSlip,
        toggleMissing,
        packageStateLabel,
    };
};

export default useBatchPackages;
